import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Activity, Zap, FolderOpen, MessageSquare, TrendingUp, Download, FileJson, FileSpreadsheet, Eye, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../hooks/useAuth';

const COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899'];

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [visitStats, setVisitStats] = useState(null);
  const isAdmin = user?.email === 'mikeray78lovee@gmail.com';

  const handleExport = async (format) => {
    setExporting(format);
    try {
      if (format === 'json') {
        const res = await api.get('/analytics/export?format=json');
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `supertool_analytics_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const res = await api.get('/analytics/export?format=csv', { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `supertool_analytics_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast({ title: 'Export successful', description: `Downloaded as ${format.toUpperCase()}` });
    } catch (err) {
      toast({ title: 'Export failed', description: err.message, variant: 'destructive' });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await api.get('/analytics');
        setData(res.data);
      } catch (err) {
        console.error('Failed to load analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();

    // Fetch visit stats for admin
    if (isAdmin) {
      api.get('/admin/stats').then(res => setVisitStats(res.data)).catch(() => {});
    }
  }, [isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const modelData = data?.modelUsage ? Object.entries(data.modelUsage).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value })) : [];
  const statusData = data?.projectStatuses ? Object.entries(data.projectStatuses).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value })) : [];
  const creditHistory = data?.creditHistory || [];

  const statCards = [
    { label: 'Total Projects', value: data?.totalProjects || 0, icon: FolderOpen, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'AI Messages', value: data?.totalMessages || 0, icon: MessageSquare, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { label: 'Credits Remaining', value: data?.creditsRemaining || 0, icon: Zap, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { label: 'Burn Rate', value: `${data?.burnRate || 0}/day`, icon: TrendingUp, color: 'text-rose-400', bg: 'bg-rose-500/10' },
    { label: 'Days Left', value: data?.daysRemaining > 900 ? '∞' : data?.daysRemaining || '—', icon: Activity, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { label: 'Current Plan', value: data?.plan || 'Free', icon: TrendingUp, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    ...(isAdmin && visitStats ? [
      { label: 'Page Visits', value: visitStats.totalVisits, icon: Eye, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
      { label: 'Unique Visitors', value: visitStats.uniqueVisitors, icon: Globe, color: 'text-pink-400', bg: 'bg-pink-500/10' }
    ] : [])
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-secondary border border-input rounded-lg px-3 py-2 shadow-lg">
          <p className="text-foreground text-sm font-medium">{label}</p>
          <p className="text-blue-400 text-sm">{payload[0].value} credits</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-2 text-foreground/80 hover:text-foreground hover:bg-accent" data-testid="analytics-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
              <p className="text-muted-foreground mt-0.5">Track your usage, credits, and project statistics</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('json')}
              disabled={exporting === 'json'}
              className="border-input bg-muted text-foreground/80 hover:text-foreground hover:bg-accent hover:border-blue-500/40"
              data-testid="export-json-btn"
            >
              <FileJson className="w-4 h-4 mr-2" />
              {exporting === 'json' ? 'Exporting...' : 'Export JSON'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={exporting === 'csv'}
              className="border-input bg-muted text-foreground/80 hover:text-foreground hover:bg-accent hover:border-emerald-500/40"
              data-testid="export-csv-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="analytics-stat-cards">
          {statCards.map((stat) => (
            <Card key={stat.label} className="bg-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{stat.label}</p>
                    <p className={`text-2xl font-bold ${stat.color}`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Credit Usage Chart */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2"><Zap className="w-5 h-5 text-amber-400" />Credit Usage (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent data-testid="credit-usage-chart">
              {creditHistory.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={creditHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222222" />
                    <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={{ stroke: '#222222' }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={{ stroke: '#222222' }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="credits" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex items-center justify-center text-muted-foreground">No data yet. Start chatting to see usage.</div>
              )}
            </CardContent>
          </Card>

          {/* AI Model Breakdown */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2"><BarChart3 className="w-5 h-5 text-purple-400" />AI Model Usage</CardTitle>
            </CardHeader>
            <CardContent data-testid="model-usage-chart">
              {modelData.length > 0 ? (
                <div className="flex items-center gap-6">
                  <ResponsiveContainer width="50%" height={220}>
                    <PieChart>
                      <Pie data={modelData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={4} dataKey="value">
                        {modelData.map((_, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: '8px', color: 'white' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-3">
                    {modelData.map((item, i) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-sm text-foreground/80">{item.name}</span>
                        </div>
                        <span className="text-sm font-semibold text-foreground">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[220px] flex items-center justify-center text-muted-foreground">No model usage data yet.</div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Project Status */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2"><FolderOpen className="w-5 h-5 text-blue-400" />Project Status Breakdown</CardTitle>
            </CardHeader>
            <CardContent data-testid="project-status-chart">
              {statusData.length > 0 ? (
                <div className="space-y-3">
                  {statusData.map((item, i) => {
                    const total = statusData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
                    return (
                      <div key={item.name}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-foreground/80">{item.name}</span>
                          <span className="text-sm text-foreground font-semibold">{item.value} ({pct}%)</span>
                        </div>
                        <div className="w-full h-2.5 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center text-muted-foreground">No projects yet.</div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-400" />Recent Activity</CardTitle>
            </CardHeader>
            <CardContent data-testid="recent-activity-list">
              {data?.recentActivity?.length > 0 ? (
                <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
                  {data.recentActivity.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-muted rounded-lg border border-border">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${item.type === 'create' ? 'bg-emerald-400' : item.type === 'update' ? 'bg-blue-400' : 'bg-amber-400'}`} />
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{item.message}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{new Date(item.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center text-muted-foreground">No recent activity.</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Per-Project Cost Breakdown */}
        {data?.projectBreakdown?.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2"><Zap className="w-5 h-5 text-rose-400" />Per-Project Cost Breakdown</CardTitle>
            </CardHeader>
            <CardContent data-testid="project-cost-breakdown">
              <div className="space-y-3">
                {data.projectBreakdown.map((p) => {
                  const maxMsg = Math.max(...data.projectBreakdown.map(x => x.messages), 1);
                  const pct = Math.round((p.messages / maxMsg) * 100);
                  return (
                    <div key={p.id}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground font-medium">{p.name}</span>
                          <span className="text-xs text-muted-foreground capitalize">{p.model}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-muted-foreground">{p.messages} msgs</span>
                          <span className="text-amber-400 font-semibold">${p.cost}</span>
                        </div>
                      </div>
                      <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
